/**
 * The widget library.
 *
 * Every widget paints itself on the shared render loop straight from the signal
 * store. None of them hold value state in React, so adding widgets costs paint
 * time and nothing else.
 */
import { useCallback, useRef } from 'react';
import { GEAR_NEUTRAL, PID_FOR_SIGNAL, SCOPE_SHORT, type ObdSession, type SignalDef, type SignalState } from '@obd/core';
import type { Widget } from '../store/appState';
import { seriesColor, theme, useCanvasPainter } from './render';
import {
  FONT, drawSeriesPanel, drawSparkline, fitText, formatValue, niceTicks, rangeFor, shortNum,
  statusFor, valueAtTime,
} from './draw';

export interface WidgetProps {
  widget: Widget;
  session: ObdSession;
}

/**
 * Resolve a signal, or undefined if this car does not report it.
 *
 * Every known PID is registered with the store up front so the picker can list
 * them all, which means presence in the store says nothing about whether the
 * car actually has the sensor. Once discovery has run, a raw signal whose PID
 * is absent from the support list has to read as "not reported": otherwise a
 * car that simply does not measure, say, oil temperature is indistinguishable
 * from one whose reading has not arrived yet, and both sit at "--" for ever.
 */
function resolve(session: ObdSession, key: string | undefined): SignalState | undefined {
  if (!key) return undefined;
  const state = session.store.get(key);
  if (!state) return undefined;
  const def = PID_FOR_SIGNAL.get(key);
  const supported = session.supportedPids;
  if (def && supported.size > 0 && !supported.has(def.pid)) return undefined;
  return state;
}

/** Resolve a widget's primary signal, or undefined if the car lacks it. */
function primary(session: ObdSession, widget: Widget): SignalState | undefined {
  return resolve(session, widget.signals[0]);
}

/**
 * What a widget should show: the live reading, or the figure over a span.
 *
 * A widget set to a span reads the same value a trip computer would: fuel over
 * distance for consumption, a time-weighted mean for everything else.
 */
export function widgetValue(session: ObdSession, widget: Widget, state: SignalState): number | undefined {
  const scope = widget.scope;
  return !scope || scope === 'now' ? state.value : session.scopedValue(state.def.key, scope);
}

/** The label a widget carries when it is not showing the live reading. */
export function scopeTag(widget: Widget): string {
  const scope = widget.scope;
  return !scope || scope === 'now' ? '' : ' · ' + SCOPE_SHORT[scope].toLowerCase();
}

function Unavailable({ label }: { label: string }): JSX.Element {
  return (
    <div className="widget-empty">
      <span>{label}</span>
      <span className="muted">not reported</span>
    </div>
  );
}

// ------------------------------------------------------------------- gauge

export function GaugeWidget({ widget, session }: WidgetProps): JSX.Element {
  const state = primary(session, widget);

  const paint = useCallback(
    (ctx: CanvasRenderingContext2D, w: number, h: number) => {
      if (!state) return;
      const def = state.def;
      const [lo, hi] = rangeFor(def, widget.min, widget.max);
      const value = widgetValue(session, widget, state);

      const cx = w / 2;
      const cy = h * 0.58;
      const radius = Math.min(w * 0.44, h * 0.46);
      if (radius < 12) return;

      const START = Math.PI * 0.75;
      const SWEEP = Math.PI * 1.5;
      const frac = value === undefined ? 0 : Math.max(0, Math.min(1, (value - lo) / (hi - lo)));
      const arcWidth = Math.max(5, radius * 0.13);

      // Track
      ctx.beginPath();
      ctx.arc(cx, cy, radius, START, START + SWEEP);
      ctx.strokeStyle = theme.get('--grid', '#2c2c2a');
      ctx.lineWidth = arcWidth;
      ctx.lineCap = 'butt';
      ctx.stroke();

      // Warn zone, drawn on the track so it reads as part of the scale.
      const warn = def.warnAbove;
      if (warn !== undefined && warn > lo && warn < hi) {
        const warnFrac = (warn - lo) / (hi - lo);
        ctx.beginPath();
        ctx.arc(cx, cy, radius, START + SWEEP * warnFrac, START + SWEEP);
        ctx.strokeStyle = theme.get('--critical', '#d03b3b');
        ctx.globalAlpha = 0.28;
        ctx.stroke();
        ctx.globalAlpha = 1;
      }

      // Ticks
      const ticks = niceTicks(lo, hi, 5);
      ctx.font = Math.max(8, radius * 0.13) + 'px ' + FONT;
      ctx.fillStyle = theme.get('--ink-muted', '#898781');
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      for (const tick of ticks) {
        if (tick < lo || tick > hi) continue;
        const a = START + SWEEP * ((tick - lo) / (hi - lo));
        const inner = radius - arcWidth / 2 - 3;
        const outer = radius - arcWidth / 2 - 8;
        ctx.beginPath();
        ctx.moveTo(cx + Math.cos(a) * inner, cy + Math.sin(a) * inner);
        ctx.lineTo(cx + Math.cos(a) * outer, cy + Math.sin(a) * outer);
        ctx.strokeStyle = theme.get('--axis', '#383835');
        ctx.lineWidth = 1;
        ctx.stroke();
        if (radius > 46) {
          const lr = radius - arcWidth / 2 - 18;
          ctx.fillText(shortNum(tick), cx + Math.cos(a) * lr, cy + Math.sin(a) * lr);
        }
      }

      // Value arc
      if (value !== undefined && frac > 0.001) {
        ctx.beginPath();
        ctx.arc(cx, cy, radius, START, START + SWEEP * frac);
        ctx.strokeStyle = statusFor(value, def) ?? theme.get('--accent', '#3987e5');
        ctx.lineWidth = arcWidth;
        ctx.lineCap = 'round';
        ctx.stroke();
      }

      // Hero figure. System sans, proportional figures, no display face.
      ctx.textAlign = 'center';
      ctx.textBaseline = 'alphabetic';
      ctx.fillStyle = theme.get('--ink-primary', '#fff');
      ctx.font = '600 ' + Math.max(15, radius * 0.46) + 'px ' + FONT;
      ctx.fillText(formatValue(value, def), cx, cy + radius * 0.14);

      ctx.font = Math.max(9, radius * 0.16) + 'px ' + FONT;
      ctx.fillStyle = theme.get('--ink-muted', '#898781');
      ctx.fillText(def.unit, cx, cy + radius * 0.42);
    },
    [state, widget, session],
  );

  const ref = useCanvasPainter(paint, 16);
  if (!state) return <Unavailable label={widget.signals[0] ?? 'signal'} />;
  return (
    <>
      <WidgetTitle def={state.def} widget={widget} />
      <canvas ref={ref} className="widget-canvas" />
    </>
  );
}

// --------------------------------------------------------------------- bar

export function BarWidget({ widget, session }: WidgetProps): JSX.Element {
  const state = primary(session, widget);

  const paint = useCallback(
    (ctx: CanvasRenderingContext2D, w: number, h: number) => {
      if (!state) return;
      const def = state.def;
      const [lo, hi] = rangeFor(def, widget.min, widget.max);
      const value = widgetValue(session, widget, state);

      const barH = Math.min(10, h * 0.34);
      const barY = h - barH - 2;
      const labelY = barY - 6;

      ctx.font = '11.5px ' + FONT;
      ctx.textBaseline = 'alphabetic';
      ctx.textAlign = 'left';
      ctx.fillStyle = theme.get('--ink-secondary', '#c3c2b7');
      ctx.fillText(fitText(ctx, def.short, w * 0.6), 0, labelY);

      ctx.textAlign = 'right';
      ctx.fillStyle = theme.get('--ink-primary', '#fff');
      ctx.font = '600 12.5px ' + FONT;
      ctx.fillText(formatValue(value, def) + (def.unit ? ' ' + def.unit : ''), w, labelY);

      // Track
      ctx.fillStyle = theme.get('--grid', '#2c2c2a');
      ctx.beginPath();
      ctx.roundRect(0, barY, w, barH, barH / 2);
      ctx.fill();

      if (value === undefined) return;

      // Signed signals (fuel trims) grow out from a zero line in the middle.
      const signed = lo < 0 && hi > 0;
      const clamp = Math.max(lo, Math.min(hi, value));
      const zeroX = signed ? ((0 - lo) / (hi - lo)) * w : 0;
      const valX = ((clamp - lo) / (hi - lo)) * w;
      const x = Math.min(zeroX, valX);
      const barW = Math.max(Math.abs(valX - zeroX), 2);

      ctx.fillStyle = statusFor(value, def) ?? theme.get('--accent', '#3987e5');
      ctx.beginPath();
      // 4px rounded data-end, anchored to the baseline.
      ctx.roundRect(x, barY, barW, barH, barH / 2);
      ctx.fill();

      if (signed) {
        ctx.strokeStyle = theme.get('--axis', '#383835');
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.moveTo(Math.round(zeroX) + 0.5, barY - 2);
        ctx.lineTo(Math.round(zeroX) + 0.5, barY + barH + 2);
        ctx.stroke();
      }
    },
    [state, widget, session],
  );

  const ref = useCanvasPainter(paint, 33);
  if (!state) return <Unavailable label={widget.signals[0] ?? 'signal'} />;
  return <canvas ref={ref} className="widget-canvas" />;
}

// ------------------------------------------------------------------ number

export function NumberWidget({ widget, session }: WidgetProps): JSX.Element {
  const state = primary(session, widget);
  const scoped = widget.scope !== undefined && widget.scope !== 'now';

  const paint = useCallback(
    (ctx: CanvasRenderingContext2D, w: number, h: number) => {
      if (!state) return;
      const def = state.def;
      const value = widgetValue(session, widget, state);
      // A running average next to a live trace of the same signal reads as a
      // contradiction, so the trace belongs to the live reading only.
      const sparkH = widget.sparkline && !scoped ? Math.min(26, h * 0.3) : 0;
      const numberArea = h - sparkH;

      ctx.textAlign = 'left';
      ctx.textBaseline = 'top';
      ctx.font = '11px ' + FONT;
      ctx.fillStyle = theme.get('--ink-muted', '#898781');
      ctx.fillText(fitText(ctx, def.short + scopeTag(widget), w), 0, 0);

      const size = Math.max(18, Math.min(numberArea * 0.5, w * 0.32));
      ctx.font = '600 ' + size + 'px ' + FONT;
      ctx.textBaseline = 'alphabetic';
      ctx.fillStyle = statusFor(value, def) ?? theme.get('--ink-primary', '#fff');
      const text = formatValue(value, def);
      ctx.fillText(text, 0, numberArea * 0.78);

      const numberWidth = ctx.measureText(text).width;
      if (def.unit) {
        ctx.font = Math.max(10, size * 0.34) + 'px ' + FONT;
        ctx.fillStyle = theme.get('--ink-muted', '#898781');
        ctx.fillText(def.unit, numberWidth + 5, numberArea * 0.78);
      }

      if (sparkH > 4) {
        drawSparkline(ctx, state.history, 0, numberArea, w, sparkH - 2, {
          points: 120,
          color: theme.get('--accent', '#3987e5'),
          fill: true,
        });
      }
    },
    [state, widget, session, scoped],
  );

  const ref = useCanvasPainter(paint, 33);
  if (!state) return <Unavailable label={widget.signals[0] ?? 'signal'} />;
  return <canvas ref={ref} className="widget-canvas" />;
}

// -------------------------------------------------------------- line chart

/**
 * Time-series chart.
 *
 * One signal gets a single panel. Several signals get stacked panels sharing
 * the time axis rather than a shared y-axis, because RPM and speed on one
 * scale would either flatten one series or require a second axis -- and a
 * second y-scale invents correlations that are not in the data.
 */
export function LineWidget({ widget, session }: WidgetProps): JSX.Element {
  const hoverRef = useRef<{ x: number; y: number } | null>(null);
  const states = widget.signals
    .map((k) => resolve(session, k))
    .filter((s): s is SignalState => s !== undefined);

  const paint = useCallback(
    (ctx: CanvasRenderingContext2D, w: number, h: number, now: number) => {
      if (states.length === 0) return;
      const windowS = widget.windowS ?? 90;
      const t1 = Date.now();
      const t0 = t1 - windowS * 1000;

      const axisW = 34;
      const legendH = 16;
      const timeAxisH = 14;
      const gap = 6;
      const plotX = axisW;
      const plotW = Math.max(10, w - axisW - 6);
      const areaTop = legendH;
      const areaH = h - legendH - timeAxisH;
      const panelH = (areaH - gap * (states.length - 1)) / states.length;
      if (panelH < 8) return;

      // Legend: always present for two or more series, so identity is never
      // carried by colour alone.
      ctx.font = '10.5px ' + FONT;
      ctx.textBaseline = 'middle';
      ctx.textAlign = 'left';
      let lx = plotX;
      states.forEach((s, i) => {
        const color = seriesColor(i);
        const label = s.def.short + (s.def.unit ? ' (' + s.def.unit + ')' : '');
        const tw = ctx.measureText(label).width;
        if (lx + tw + 16 > w) return;
        ctx.fillStyle = color;
        ctx.beginPath();
        ctx.arc(lx + 4, legendH / 2, 3.5, 0, Math.PI * 2);
        ctx.fill();
        ctx.fillStyle = theme.get('--ink-secondary', '#c3c2b7');
        ctx.fillText(label, lx + 11, legendH / 2);
        lx += tw + 22;
      });

      states.forEach((s, i) => {
        const y = areaTop + i * (panelH + gap);
        drawSeriesPanel(ctx, s, { x: plotX, y, w: plotW, h: panelH }, { t0, t1 }, seriesColor(i), {
          maxPoints: 2000,
        });
      });

      // Time axis
      ctx.strokeStyle = theme.get('--axis', '#383835');
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(plotX, Math.round(areaTop + areaH) + 0.5);
      ctx.lineTo(plotX + plotW, Math.round(areaTop + areaH) + 0.5);
      ctx.stroke();

      ctx.fillStyle = theme.get('--ink-muted', '#898781');
      ctx.font = '10px ' + FONT;
      ctx.textBaseline = 'top';
      ctx.textAlign = 'left';
      ctx.fillText('-' + windowS + 's', plotX, areaTop + areaH + 3);
      ctx.textAlign = 'right';
      ctx.fillText('now', plotX + plotW, areaTop + areaH + 3);

      // Crosshair and readout. An HTML chart is interactive by default.
      const hover = hoverRef.current;
      if (hover && hover.x >= plotX && hover.x <= plotX + plotW) {
        const t = t0 + ((hover.x - plotX) / plotW) * (t1 - t0);
        ctx.strokeStyle = theme.get('--hairline-strong', 'rgba(255,255,255,0.18)');
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.moveTo(Math.round(hover.x) + 0.5, areaTop);
        ctx.lineTo(Math.round(hover.x) + 0.5, areaTop + areaH);
        ctx.stroke();

        const lines = states.map((s, i) => ({
          color: seriesColor(i),
          text: s.def.short + '  ' + formatValue(valueAtTime(s.history, t), s.def) + ' ' + s.def.unit,
        }));
        const boxW = Math.max(...lines.map((l) => ctx.measureText(l.text).width)) + 24;
        const boxH = lines.length * 15 + 8;
        const bx = Math.min(hover.x + 10, w - boxW - 2);
        const by = Math.min(Math.max(areaTop, hover.y - boxH / 2), areaTop + areaH - boxH);

        ctx.fillStyle = theme.get('--surface-3', '#26282d');
        ctx.strokeStyle = theme.get('--hairline', 'rgba(255,255,255,0.1)');
        ctx.beginPath();
        ctx.roundRect(bx, by, boxW, boxH, 6);
        ctx.fill();
        ctx.stroke();

        ctx.textBaseline = 'middle';
        ctx.textAlign = 'left';
        lines.forEach((l, i) => {
          const ly = by + 12 + i * 15;
          ctx.fillStyle = l.color;
          ctx.beginPath();
          ctx.arc(bx + 9, ly, 3, 0, Math.PI * 2);
          ctx.fill();
          // Text wears text tokens; the dot beside it carries identity.
          ctx.fillStyle = theme.get('--ink-primary', '#fff');
          ctx.fillText(l.text, bx + 16, ly);
        });
      }
      void now;
    },
    [states, widget.windowS],
  );

  const ref = useCanvasPainter(paint, 33);

  const onMove = (e: React.PointerEvent<HTMLCanvasElement>): void => {
    const rect = e.currentTarget.getBoundingClientRect();
    hoverRef.current = { x: e.clientX - rect.left, y: e.clientY - rect.top };
  };
  const onLeave = (): void => {
    hoverRef.current = null;
  };

  if (states.length === 0) return <Unavailable label={widget.signals.join(', ') || 'signals'} />;
  return (
    <canvas
      ref={ref}
      className="widget-canvas"
      onPointerMove={onMove}
      onPointerLeave={onLeave}
      style={{ touchAction: 'pan-y' }}
    />
  );
}

// --------------------------------------------------------------- score ring

export function ScoreWidget({ widget, session }: WidgetProps): JSX.Element {
  const state = primary(session, widget);

  const paint = useCallback(
    (ctx: CanvasRenderingContext2D, w: number, h: number) => {
      if (!state) return;
      const value = state.value ?? 0;
      const cx = w / 2;
      const cy = h / 2;
      const radius = Math.min(w, h) * 0.36;
      const arcWidth = Math.max(6, radius * 0.18);

      ctx.beginPath();
      ctx.arc(cx, cy, radius, -Math.PI / 2, Math.PI * 1.5);
      ctx.strokeStyle = theme.get('--grid', '#2c2c2a');
      ctx.lineWidth = arcWidth;
      ctx.stroke();

      // Score is a state, so it wears status colours rather than a series hue.
      const tone =
        value >= 80 ? theme.get('--good', '#0ca30c')
          : value >= 60 ? theme.get('--warning', '#fab219')
            : value >= 40 ? theme.get('--serious', '#ec835a')
              : theme.get('--critical', '#d03b3b');

      ctx.beginPath();
      ctx.arc(cx, cy, radius, -Math.PI / 2, -Math.PI / 2 + (Math.PI * 2 * value) / 100);
      ctx.strokeStyle = tone;
      ctx.lineWidth = arcWidth;
      ctx.lineCap = 'round';
      ctx.stroke();

      ctx.textAlign = 'center';
      ctx.textBaseline = 'alphabetic';
      ctx.fillStyle = theme.get('--ink-primary', '#fff');
      ctx.font = '600 ' + Math.max(16, radius * 0.62) + 'px ' + FONT;
      ctx.fillText(value.toFixed(0), cx, cy + radius * 0.2);
      ctx.font = Math.max(9, radius * 0.2) + 'px ' + FONT;
      ctx.fillStyle = theme.get('--ink-muted', '#898781');
      ctx.fillText('/ 100', cx, cy + radius * 0.55);
    },
    [state],
  );

  const ref = useCanvasPainter(paint, 100);
  if (!state) return <Unavailable label="score" />;
  return (
    <>
      <WidgetTitle def={state.def} widget={widget} />
      <canvas ref={ref} className="widget-canvas" />
    </>
  );
}

// ------------------------------------------------------------------- gear

export function GearWidget({ session }: WidgetProps): JSX.Element {
  const state = session.store.get('gear');

  const paint = useCallback(
    (ctx: CanvasRenderingContext2D, w: number, h: number) => {
      const value = state?.value;
      // Below zero is a gear that is engaged but not identified yet.
      const label =
        value === undefined ? '--' : value === GEAR_NEUTRAL ? 'N' : value < 0 ? '–' : String(Math.round(value));
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillStyle = theme.get('--ink-primary', '#fff');
      ctx.font = '600 ' + Math.min(w * 0.55, h * 0.62) + 'px ' + FONT;
      ctx.fillText(label, w / 2, h * 0.54);
      ctx.font = '10px ' + FONT;
      ctx.fillStyle = theme.get('--ink-muted', '#898781');
      ctx.textBaseline = 'top';
      ctx.fillText('GEAR', w / 2, 2);
    },
    [state],
  );

  const ref = useCanvasPainter(paint, 100);
  return <canvas ref={ref} className="widget-canvas" />;
}

// -------------------------------------------------------------- shift light

/**
 * Segmented shift indicator. Fills from the economy shift point up to the
 * redline, so it reads as "you are past where you meant to change up".
 */
export function ShiftWidget({ session }: WidgetProps): JSX.Element {
  const state = session.store.get('rpm');

  const paint = useCallback(
    (ctx: CanvasRenderingContext2D, w: number, h: number) => {
      const rpm = state?.value ?? 0;
      const { ecoShiftRpm, redlineRpm } = session.vehicle;
      const lo = ecoShiftRpm * 0.6;
      const frac = Math.max(0, Math.min(1, (rpm - lo) / Math.max(redlineRpm - lo, 1)));

      const segments = 10;
      const gap = 3;
      const segW = (w - gap * (segments - 1)) / segments;
      const barH = Math.min(h * 0.42, 22);
      const y = h * 0.4;

      for (let i = 0; i < segments; i++) {
        const on = frac > i / segments;
        const t = i / (segments - 1);
        const color = t < 0.5
          ? theme.get('--good', '#0ca30c')
          : t < 0.8
            ? theme.get('--warning', '#fab219')
            : theme.get('--critical', '#d03b3b');
        ctx.fillStyle = on ? color : theme.get('--grid', '#2c2c2a');
        ctx.beginPath();
        // 2px surface gap between fills rather than borders around them.
        ctx.roundRect(i * (segW + gap), y, segW, barH, 2);
        ctx.fill();
      }

      ctx.font = '10px ' + FONT;
      ctx.fillStyle = theme.get('--ink-muted', '#898781');
      ctx.textAlign = 'left';
      ctx.textBaseline = 'top';
      ctx.fillText('SHIFT', 0, 2);

      const advice = session.store.value('shift_advice');
      if (advice === 1) {
        ctx.textAlign = 'right';
        ctx.fillStyle = theme.get('--warning', '#fab219');
        ctx.fillText('change up', w, 2);
      }
    },
    [state, session],
  );

  const ref = useCanvasPainter(paint, 33);
  return <canvas ref={ref} className="widget-canvas" />;
}

// ------------------------------------------------------------------- light

export function LightWidget({ widget, session }: WidgetProps): JSX.Element {
  const state = primary(session, widget);

  const paint = useCallback(
    (ctx: CanvasRenderingContext2D, w: number, h: number) => {
      if (!state) return;
      const def = state.def;
      const value = state.value;
      const on = (value ?? 0) > 0.5;
      const r = Math.min(w, h) * 0.18;

      ctx.beginPath();
      ctx.arc(r + 4, h / 2, r, 0, Math.PI * 2);
      ctx.fillStyle = on ? theme.get('--critical', '#d03b3b') : theme.get('--grid', '#2c2c2a');
      ctx.fill();

      ctx.font = '12px ' + FONT;
      ctx.textAlign = 'left';
      ctx.textBaseline = 'middle';
      ctx.fillStyle = theme.get('--ink-secondary', '#c3c2b7');
      // Never colour alone: the state is spelled out beside the lamp.
      const label = def.enumLabels?.[Math.round(value ?? 0)] ?? (on ? 'ON' : 'off');
      ctx.fillText(fitText(ctx, def.short + ' — ' + label, w - r * 2 - 14), r * 2 + 12, h / 2);
    },
    [state],
  );

  const ref = useCanvasPainter(paint, 100);
  if (!state) return <Unavailable label={widget.signals[0] ?? 'signal'} />;
  return <canvas ref={ref} className="widget-canvas" />;
}

function WidgetTitle({ def, widget }: { def: SignalDef; widget: Widget }): JSX.Element {
  return <div className="widget-title">{widget.title ?? def.name}</div>;
}

export const WIDGET_COMPONENTS: Record<Widget['type'], (p: WidgetProps) => JSX.Element> = {
  gauge: GaugeWidget,
  bar: BarWidget,
  number: NumberWidget,
  line: LineWidget,
  light: LightWidget,
  score: ScoreWidget,
  gear: GearWidget,
  shift: ShiftWidget,
};
